import { Group, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { Tarifart } from '@/api/types';
import {
  AUSBILDUNG_EG_OPTIONS,
  isAnerkennungsjahrEg,
  isPivaEg,
} from '@/constants/ausbildung';

const SUE_EG_OPTIONS: string[] = [
  'S2',
  'S3',
  'S4',
  'S8a',
  'S8b',
  'S9',
  'S11a',
  'S11b',
  'S12',
  'S13',
  'S14',
  'S15',
  'S16',
  'S17',
  'S18',
];

// TVöD-VKA-Entgeltgruppen aus VKA-Entgeltgruppen.xlsx (Pilot-Träger).
const VKA_EG_OPTIONS: string[] = [
  'EG 1',
  'EG 2',
  'EG 2Ü',
  'EG 3',
  'EG 4',
  'EG 5',
  'EG 6',
  'EG 7',
  'EG 8',
  'EG 9a',
  'EG 9b',
  'EG 9c',
  'EG 10',
  'EG 11',
  'EG 12',
  'EG 13',
  'EG 14',
  'EG 15',
  'EG 15Ü',
];

const STUFE_OPTIONS_DEFAULT: string[] = ['1', '2', '3', '4', '5', '6'];
const STUFE_OPTIONS_PIVA: string[] = ['1', '2', '3'];

export interface EgGrStufeValue {
  eg: string | null;
  stufe: string | null;
}

interface EgGrStufeSelectorProps {
  value: EgGrStufeValue;
  onChange: (value: EgGrStufeValue) => void;
  disabled?: boolean;
  tarifart?: Tarifart;
  /** Stufe ausblenden, weil sie sich woanders ergibt.
   *
   * Bei Ausbildungen mit hinterlegtem Zeitraum ist die Stufe das laufende
   * Ausbildungsjahr — es wechselt mitten im Foerderjahr und laesst sich
   * darum nicht als fester Wert waehlen. */
  stufeVerborgen?: boolean;
}

// Mapping vom kanonischen EG-Wert (DB-Wert, bleibt unverändert) auf den
// i18n-Schlüssel des ausgeschriebenen Labels für das Dropdown.
const AUSBILDUNG_EG_LABEL_KEY: Record<string, string> = {
  'I-SozPäd': 'stelle.ausbildung.eg.sozpaed',
  'II-Erz': 'stelle.ausbildung.eg.erz',
  'III-KiPfl': 'stelle.ausbildung.eg.kipfl',
  'IV-PivA': 'stelle.ausbildung.eg.piva',
};

export function EgGrStufeSelector({
  value,
  onChange,
  disabled,
  tarifart = 'sue',
  stufeVerborgen = false,
}: EgGrStufeSelectorProps) {
  const { t } = useTranslation('personal');
  const isAusbildung = tarifart === 'ausbildung';
  const isPivaSelected = isAusbildung && isPivaEg(value.eg);
  // EG-Daten: bei Ausbildung mit ausgeschriebenem Label, sonst nur Code.
  const egData = isAusbildung
    ? AUSBILDUNG_EG_OPTIONS.map((code) => ({
        value: code,
        label: t(AUSBILDUNG_EG_LABEL_KEY[code] ?? code),
      }))
    : tarifart === 'vka'
      ? VKA_EG_OPTIONS
      : SUE_EG_OPTIONS;
  // Bei Ausbildung:
  //  - Anerkennungsjahr-EGs haben keine Stufe (Feld ausgeblendet, intern '1')
  //  - IV-PivA hat exakt drei Stufen (Ausbildungsjahre)
  const stufeHidden =
    stufeVerborgen || (isAusbildung && isAnerkennungsjahrEg(value.eg));
  const stufeOptions = isPivaSelected ? STUFE_OPTIONS_PIVA : STUFE_OPTIONS_DEFAULT;
  // Bei PivA wird die "Stufe" semantisch das Ausbildungsjahr.
  const stufeLabel = isPivaSelected
    ? t('stelle.ausbildungsjahr')
    : t('stelle.stufe');
  const stufePlaceholder = isPivaSelected
    ? t('stelle.ausbildungsjahr.placeholder')
    : t('stelle.stufe.placeholder');
  return (
    <Group grow align="flex-end">
      <Select
        label={t('stelle.eg')}
        placeholder={t('stelle.eg.placeholder')}
        data={egData}
        value={value.eg}
        onChange={(eg) => {
          // Bei Wechsel zu Anerkennungsjahr-EG: Stufe defensiv auf '1'
          // setzen (Backend-Pflichtfeld); bei PivA Stufe leeren falls
          // > 3, sodass der User explizit Stufe 1/2/3 wählt.
          let nextStufe = value.stufe;
          if (eg && isAnerkennungsjahrEg(eg)) {
            nextStufe = '1';
          } else if (eg && isPivaEg(eg) && nextStufe && !STUFE_OPTIONS_PIVA.includes(nextStufe)) {
            nextStufe = null;
          }
          onChange({ eg, stufe: nextStufe });
        }}
        disabled={disabled}
        searchable
        aria-label={t('stelle.eg')}
      />
      {stufeHidden ? null : (
        <Select
          label={stufeLabel}
          placeholder={stufePlaceholder}
          data={stufeOptions}
          value={value.stufe}
          onChange={(stufe) => onChange({ ...value, stufe })}
          disabled={disabled}
          aria-label={stufeLabel}
        />
      )}
    </Group>
  );
}
